/* eslint-disable max-len */
import React, { useMemo, useRef } from 'react';
import styled from 'styled-components';
import { Link } from 'wouter';
import { useObserver } from '../../../CodesThatsGottaGoSomewhereElse';
import { Host } from '../../../Host';
import { Icon } from '../../../concepts/resources/Icon';
import { Image } from '../../../concepts/resources/Image';
import { Button, Colors, Grid } from '../../../ui';
import { useBreakpoints } from '../../../ui/Breakpoints';
import { DropButton } from '../../../ui/DropButton';
import { useResolve } from '../../Container';
import { AccountHeaderBlock } from '../../account/AccountHeaderBlock';
import { PlanStateHeaderBlock } from '../../account/PlanStateHeaderBlock';
import { useOnTeamAddUserViaPopup, useOnTeamRemoveUser } from '../../actions/WorkspaceActions';
import { Participant } from '../../canvas/participants/Participant';
import { CopyToClipboard } from '../../common/CopyToClipboard';
import { Page2 } from '../../common/Page2';
import { useHangoutUser } from '../../hangouts/helpers/HangoutUserObserver';
import { UserModel, WorkspaceModel, nameSorter } from '../Models';
import { Flexer } from '../elements/LayoutElements';

const UserPlaceholder = styled.div<{ color: string }>`
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    color: white;
    background: ${p => p.color};
`;

const UserImage = styled.div`
    flex: 0 0 5rem;
    position: relative;
    width: 5rem;
    height: 5rem;
    border-radius: .25rem;
    overflow: hidden;

    ${UserPlaceholder}, img, svg {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const UserItemInfo = styled.div`
    flex: 1 1 auto;
    padding: .25rem 0;
`;

const UserItemHeader = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: .5rem;

    color: ${Colors.textDark};
    font-size: 1.125rem;
    font-weight: 450;
    white-space: nowrap;
`;

const UserItemBody = styled.div`
    color: ${Colors.textLighter};
    font-size: .875rem;
`;

const UserItemStatus = styled.div<{ state: 'online' | 'offline' }>`
    border-radius: 50%;
    width: .5em;
    height: .5em;
    background: ${p => p.state === 'online' ? Colors.positive : 'transparent'};
    border: solid .125rem ${p => p.state === 'online' ? Colors.positive : Colors.gray400};
`;

const UserEmail = styled.div``;

const UserLocation = styled.div`
    color: ${Colors.primary};
    text-decoration: underline;
`;

const UserRemoveButton = styled.div`
    display: none;
    margin-left: auto;
`;

const UserItemFrame = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    gap: 1rem;
    padding: .5rem;
    background: white;
    border-radius: .5rem;
    border: solid .0625rem ${Colors.border};

    &:hover {
        ${UserRemoveButton} {
            display: block;
        }
    }
`;

export const useLiveLocationInfo = (user: UserModel) => {
    const workspace = useResolve(WorkspaceModel);
    const liveUser = useObserver(useHangoutUser(user.id));

    if (!!liveUser?.location) {
        const ll = liveUser.location;
        if (ll.test(`workspace:${workspace.id}/*`)) {
            if (ll.test('*/board:*')) {
                return { online: true, label: `on ${ll.param}`, href: `~/${ll.param}` };
            }
            else {
                return { online: true, label: 'in the dashboard' };
            }
        }
        else {
            return { online: true, label: 'in some other team' };
        }
    }
    else {
        return { online: false };
    }
};

const UserItem = ({ user }: { user: UserModel }) => {
    const team = useResolve(WorkspaceModel);
    const { name, email, color, photo } = useObserver(user);
    const { canTeamRemoveUser, onTeamRemoveUser } = useOnTeamRemoveUser(user.id, team);
    const liveInfo = useLiveLocationInfo(user);

    return <UserItemFrame>
        <UserImage>
            {photo && <Image src={photo} alt={name} />}
            {!photo && <UserPlaceholder color={color}>{Participant.toInitials(name)}</UserPlaceholder>}
        </UserImage>
        <UserItemInfo>
            <UserItemHeader>
                <span>{name}</span>
                <UserItemStatus state={liveInfo.online ? 'online' : 'offline'} />
                {canTeamRemoveUser &&
                    <UserRemoveButton>
                        <DropButton rounding="full" onClick={onTeamRemoveUser}>
                            <Icon size="1.25" color={Colors.text} src="deqo://icons/hero/x" />
                        </DropButton>
                    </UserRemoveButton>
                }
            </UserItemHeader>
            <UserItemBody>
                {['local', 'dev', 'test'].includes(Host.env) ? <>
                    <Flexer>
                        <CopyToClipboard value={`${Host.baseUrl}/login/fake?user=${user.id}`}>
                            <UserEmail>{email}</UserEmail>
                        </CopyToClipboard>
                    </Flexer>
                </> : <>
                    <UserEmail>{email}</UserEmail>
                </>}
                {!!liveInfo.label && liveInfo.href &&
                    <Link to={liveInfo.href}>
                        <UserLocation>{liveInfo.label}</UserLocation>
                    </Link>
                }
                {!!liveInfo.label && !liveInfo.href &&
                    <span>{liveInfo.label}</span>
                }
            </UserItemBody>
        </UserItemInfo>
    </UserItemFrame>;
};

const userLayoutBreakpoints = {
    lg: 96, md: 72, sm: 48, min: 0
};

const userLayoutCols = {
    lg: 4, md: 3, sm: 2, min: 1
};

const UserLayout = () => {

    const { members } = useObserver(useResolve(WorkspaceModel)) ?? {};
    const sorted = useMemo(() => members?.slice()?.sort(nameSorter), [members]);
    const elmt = useRef<HTMLDivElement>(null);

    const { name } = useBreakpoints(elmt, userLayoutBreakpoints);
    const cols = userLayoutCols[name];

    return <Flexer ref={elmt}>
        <Grid columns={cols} columnGap="1" rowGap="1">
            {members.map(x => <UserItem key={x.id} user={x} />)}
        </Grid>
    </Flexer>;
};

export const MembersView = () => {

    const team = useResolve(WorkspaceModel);
    const { canTeamAddUser, onTeamAddUser } = useOnTeamAddUserViaPopup(team);

    return <Page2.Scroll>
        <Page2.Header>
            <Page2.Title>
                Team Members
            </Page2.Title>
            <Page2.HeaderRight>
                <PlanStateHeaderBlock />
                <AccountHeaderBlock />
            </Page2.HeaderRight>
        </Page2.Header>
        <Page2.Content>
            <Flexer maxWidth="96">
                <Flexer height="1.75" />
                <Button disabled={!canTeamAddUser} variant="primary" onAction={onTeamAddUser}>
                    <Icon size="1.2" src="deqo://icons/hero/user-add" />&nbsp;&nbsp;Invite Members
                </Button>
                <Flexer height="2" />
                <UserLayout />
            </Flexer>
        </Page2.Content>
    </Page2.Scroll>;
};
